import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAlertRules } from '../hooks/useAlertRules';
import { getUICategory } from '../hooks/useNotifications';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { cn } from '../components/ui/Button';
import { Plus, Trash2, Bell, Smartphone, Shield, Clock, Settings, AlertTriangle, CheckCircle2, RefreshCw, TrendingUp, ArrowLeftRight } from 'lucide-react';
export function AlertRulesView() {
  const navigate = useNavigate();
  const {
    rules,
    deleteRule,
    getPreset
  } = useAlertRules();
  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteRule(id);
    }
  };
  const formatRelativeTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };
  return <div className="jua-page-bg min-h-screen p-6 md:p-8" data-id="element-2148">
      <div className="max-w-5xl mx-auto space-y-8" data-id="element-2149">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2150">
          <div data-id="element-2151">
            <div className="flex items-center gap-3" data-id="element-2152">
              <h1 className="text-2xl font-bold text-white" data-id="element-2153">Manage Alerts</h1>
              <Badge variant="pink" className="px-2 py-0.5 h-6 text-xs" data-id="element-2154">
                {rules.length} active
              </Badge>
            </div>
            <p className="text-slate-400 mt-1" data-id="element-2155">
              Manage your automated notifications and monitoring rules.
            </p>
          </div>
          <Button onClick={() => navigate('/alerts/create')} className="text-xs h-9 whitespace-nowrap" data-id="element-2156">
            <Plus size={14} className="mr-2" data-id="element-2157" />
            Create Alert
          </Button>
        </div>

        {/* Rules List */}
        <div className="space-y-4" data-id="element-2158">
          {rules.length === 0 ? <div className="py-20 flex flex-col items-center justify-center text-center bg-slate-800/20 rounded-xl border border-slate-700/30 border-dashed" data-id="element-2159">
              <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mb-4" data-id="element-2160">
                <Shield size={24} className="text-slate-600" data-id="element-2161" />
              </div>
              <h3 className="text-lg font-medium text-white mb-1" data-id="element-2162">
                No alert rules yet
              </h3>
              <p className="text-slate-500 text-sm max-w-xs mb-6" data-id="element-2163">
                Create your first alert to monitor market events, model updates,
                or forecast changes.
              </p>
              <Button onClick={() => navigate('/alerts/create')} data-id="element-2164">
                <Plus size={16} className="mr-2" data-id="element-2165" />
                Create First Alert
              </Button>
            </div> : rules.map(rule => {
          const preset = getPreset(rule.preset_id);
          const Icon = preset?.icon || AlertTriangle;
          const iconBg = preset?.glassIconBg || 'bg-white/[0.06]';
          const iconColor = preset?.glassIconColor || 'text-white/70';
          const category = getUICategory(rule.notification_type);
          return <Card key={rule.id} className="group p-4 flex flex-col md:flex-row md:items-center gap-4 bg-slate-800/40 border-slate-700/50 hover:bg-slate-800/60 transition-colors" data-id="element-2166">
                  {/* Icon */}
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border border-white/[0.08] backdrop-blur-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]', iconBg, iconColor)} data-id="element-2167">
                    <Icon size={18} data-id="element-2168" />
                  </div>

                  {/* Main Info */}
                  <div className="flex-1 min-w-0" data-id="element-2169">
                    <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-2170">
                      <h3 className="text-sm font-semibold text-white truncate" data-id="element-2171">
                        {rule.alert_name}
                      </h3>
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-slate-800 border-slate-700 text-slate-400" data-id="element-2172">
                        {category}
                      </Badge>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500" data-id="element-2173">
                      {rule.workspace_name && <span className="flex items-center gap-1.5" data-id="element-2174">
                          <div className="w-1.5 h-1.5 rounded-full bg-slate-600" data-id="element-2175" />
                          {rule.workspace_name}
                        </span>}
                      <span className="flex items-center gap-1.5" data-id="element-2176">
                        <Clock size={12} data-id="element-2177" />
                        Created {formatRelativeTime(rule.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Delivery Channels */}
                  <div className="flex items-center gap-3 px-4 py-2 bg-slate-900/30 rounded-lg border border-slate-800/50" data-id="element-2178">
                    <div className={cn('flex items-center gap-1.5 text-xs font-medium transition-colors', rule.delivery.in_app ? 'text-blue-400' : 'text-slate-600 opacity-50')} title={rule.delivery.in_app ? 'In-app notifications enabled' : 'In-app notifications disabled'} data-id="element-2179">
                      <Bell size={14} data-id="element-2180" />
                      <span className="hidden md:inline" data-id="element-2181">App</span>
                    </div>
                    <div className="w-px h-3 bg-slate-700/50" data-id="element-2182" />
                    <div className={cn('flex items-center gap-1.5 text-xs font-medium transition-colors', rule.delivery.push ? 'text-pink-400' : 'text-slate-600 opacity-50')} title={rule.delivery.push ? 'Push notifications enabled' : 'Push notifications disabled'} data-id="element-2183">
                      <Smartphone size={14} data-id="element-2184" />
                      <span className="hidden md:inline" data-id="element-2185">Push</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 md:pl-4 md:border-l md:border-slate-700/50" data-id="element-2186">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-400 hover:bg-red-500/10" onClick={() => handleDelete(rule.id, rule.alert_name)} title="Delete rule" data-id="element-2187">
                      <Trash2 size={14} data-id="element-2188" />
                    </Button>
                  </div>
                </Card>;
        })}
        </div>
      </div>
    </div>;
}